import { useEffect, useMemo, useState } from "react";
import { awards } from "../../../../engine/awards";
import { mainBoard, podium } from "../../../../engine/ranking";
import { placeAt, restMs } from "../../lib/reveal";
import { owedScores } from "../../../../engine/selectors";
import type { Config, LoggedEvent, TournamentState } from "../../../../engine/types";
import type { Role } from "../../../../shared/protocol";
import { ConfirmButton, Stepper, Toggle, clockTime, nameOf, type TabProps } from "./common";
import { AdminsSection, adminsSummary } from "./Admins";
import { Fold } from "./Fold";
import { shortLink } from "../../../../shared/short";
import { LinkRow } from "./Links";
import { WhoCard } from "./WhoCard";
import { actor, sentence } from "./words";

const mins = (v: number): string => String(Math.round(v * 100) / 100);

/** What a new link does to the screens that have the old one. */
// Decision 51 (18 Oct, his word): the wall, the courts and the players keep their links all day,
// so the short addresses sent round never go stale. Only these two can be replaced.
const REGEN: { role: Role; label: string; question: string }[] = [
  { role: "admin", label: "Owner", question: "Sure? Only the owner's phones are affected: any other phone on the owner link stops working until it has the new link. This phone moves to it by itself. The named admins keep theirs." },
  { role: "remote", label: "Watch remote", question: "Sure? The watch stops working until its phone has the new link." },
];

type Rules = Pick<Config, "gameMs" | "changeoverMs" | "target" | "winBy2" | "minGamesForPodium">;

/**
 * The rules form keeps only what this admin has changed. Everything else shows the day as it is
 * now, and only the changes are sent, so a phone that has been asleep cannot put an old game
 * length back by saving something else.
 */
function ConfigForm({ t, state, say }: TabProps) {
  const live: Rules = state.config;
  const [edits, setEdits] = useState<Partial<Rules>>({});
  const set = <K extends keyof Rules>(key: K, value: Rules[K]) => setEdits((e) => ({ ...e, [key]: value }));
  const shown = <K extends keyof Rules>(key: K): Rules[K] => (edits[key] ?? live[key]) as Rules[K];
  // an edit the day has caught up with is not a change
  const patch = Object.fromEntries(Object.entries(edits).filter(([k, v]) => v !== live[k as keyof Rules])) as Partial<Rules>;
  const dirty = Object.keys(patch).length > 0;

  return (
    <>
      <div className="ad-grid2">
        <Stepper label="Game minutes" value={shown("gameMs") / 60_000} step={0.5} min={0.5} format={mins} onChange={(v) => set("gameMs", Math.round(v * 60_000))} />
        <Stepper label="Changeover minutes" value={shown("changeoverMs") / 60_000} step={0.5} format={mins} onChange={(v) => set("changeoverMs", Math.round(v * 60_000))} />
        <Stepper label="Points to win" value={shown("target")} min={1} onChange={(v) => set("target", v)} />
        <Stepper label="Games for the podium" value={shown("minGamesForPodium")} onChange={(v) => set("minGamesForPodium", v)} />
      </div>
      <Toggle label="Win by 2" on={shown("winBy2")} onChange={(on) => set("winBy2", on)} />
      <div className="ad-grid2">
        <button type="button" className="btn" disabled={!dirty}
          onClick={() => { t.send({ type: "config.updated", patch }); setEdits({}); say("Rules sent."); }}>
          Save rules
        </button>
        <button type="button" className="btn quiet" disabled={!dirty} onClick={() => setEdits({})}>Undo edits</button>
      </div>
      <p className="ad-note">Only what you changed here is sent. A new game length counts from the next round.</p>
    </>
  );
}

type Board = keyof TournamentState["reveal"];

const REVEALS: Record<Board, { label: string; ask: string; yes: string }> = {
  awards: { label: "Hand out the awards", ask: "Hand out the awards now?", yes: "Hand them out" },
  main: { label: "Main podium on the projector", ask: "Show the podium now?", yes: "Show it" },
  fun: { label: "Fun board top three", ask: "Show the fun board now?", yes: "Show it" },
};

/** A reveal switch. Switching one on with a round still on, or scores still owed, asks first and says which. */
function RevealToggle({ t, state, board }: Pick<TabProps, "t" | "state"> & { board: Board }) {
  const { label, ask, yes } = REVEALS[board];
  const on = state.reveal[board];
  const owed = owedScores(state);
  const playing = state.clock.phase === "playing" ? state.clock.roundNo : null;
  const why = playing !== null
    ? `Round ${playing} is still being played.`
    : owed.length
      ? `${owed.length === 1 ? "A score is" : "Scores are"} still owed: ${owed.map((o) => `round ${o.roundNo} court ${o.court}`).join(", ")}.`
      : null;
  const flip = (next: boolean) => t.send({ type: "reveal", board, on: next });
  if (on || !why) return <Toggle label={label} on={on} onChange={flip} />;
  return (
    <ConfirmButton className="ad-toggle" yes={yes} question={`${why} ${ask}`} onConfirm={() => flip(true)}
      label={<><span className="ad-toggle-label">{label}</span><span className="ad-toggle-state">Off</span></>} />
  );
}

const PLACE_WORD: Record<number, string> = { 1: "1st place", 2: "2nd place", 3: "3rd place" };

/**
 * The reveal, stepped from the owner's phone (decision 53, his ask: "let me as owner control and
 * trigger things"): Next hands out each award in turn, then 3rd, 2nd and 1st on the podium, the
 * winner with the anthem. Helpers see it greyed; the server refuses it from anyone but the owner.
 */
function RevealSteps({ t, state }: Pick<TabProps, "t" | "state">) {
  const owner = t.identity?.role === "admin" && t.identity.owner !== false;
  const list = useMemo(() => awards(state, "wall"), [state]);
  const top = useMemo(() => podium(mainBoard(state)).slice(0, 3), [state]);
  const rs = state.revealStep ?? null;
  // counted on this screen when the reveal starts, then kept, so the steps cannot shift under it
  const of = rs ? rs.of : list.length;
  const places = rs ? rs.places : top.length;
  const last = of + Math.max(places, 1);
  const step = rs?.step ?? 0;
  // after a press the keys rest until the wall has rolled and shown it: a double tap cannot skip a step
  const [resting, setResting] = useState(0);
  useEffect(() => {
    if (!resting) return;
    const id = setTimeout(() => setResting(0), resting);
    return () => clearTimeout(id);
  }, [resting]);
  const go = (to: number | null) => {
    setResting(restMs(step, to, of, places));
    t.send({ type: "reveal.step", step: to, of, places });
  };
  const what = (n: number): string => {
    if (n <= of) return `Award ${n} of ${of}: ${list[n - 1]?.title ?? "the last award"}`;
    const place = placeAt(n, of, places);
    if (place === null) return "The podium (nobody has played enough games)";
    return `The podium: ${PLACE_WORD[place] ?? ""}${place === 1 ? ", with the anthem" : ""}`;
  };
  const now = step === 0 ? "Not started. The wall is as it was." : `On the wall: ${what(step)}.`;
  const owed = owedScores(state);
  const playing = state.clock.phase === "playing" ? state.clock.roundNo : null;
  const why = playing !== null
    ? `Round ${playing} is still being played.`
    : owed.length
      ? `${owed.length === 1 ? "A score is" : "Scores are"} still owed: ${owed.map((o) => `round ${o.roundNo} court ${o.court}`).join(", ")}.`
      : null;
  const off = !owner || resting > 0;
  const nextLabel = step >= last ? "All shown" : `Next: ${what(step + 1)}`;
  return (
    <section className={`ad-card ad-reveal-steps${owner ? "" : " ad-greyed"}`}>
      <h2>The reveal</h2>
      <p className="ad-note"><b>{now}</b></p>
      {step === 0 && why ? (
        <ConfirmButton className="btn accent ad-wide" disabled={off} yes="Start it" question={`${why} The awards and the podium are counted now and kept, so a score that comes in later will not change them. Start the reveal now?`}
          label={nextLabel} onConfirm={() => go(1)} />
      ) : (
        <button type="button" className="btn accent ad-wide" disabled={off || step >= last} onClick={() => go(step + 1)}>
          {nextLabel}
        </button>
      )}
      <div className="ad-row">
        <button type="button" className="btn quiet" disabled={off || step <= 1} onClick={() => go(step - 1)}>
          Back
        </button>
        <ConfirmButton className="btn quiet" disabled={off || step === 0} yes="Take it off"
          question="Take the reveal off the wall? The awards' fun points stop counting until it starts again."
          label="Take it off the wall" onConfirm={() => go(null)} />
      </div>
      <p className="ad-note">
        {owner
          ? `${of} ${of === 1 ? "award" : "awards"}, then ${places >= 3 ? "3rd, 2nd and 1st" : places === 2 ? "2nd and 1st" : places === 1 ? "1st" : "the podium"}. Each Next puts one up, with its music. The wall needs one tap from you first for its sound.`
          : "Only Abhi's phone runs the awards and the podium."}
      </p>
    </section>
  );
}

/** How many events to ask the server for, and how many of them (the server's own left out) to show. */
const LOG_ASK = 200;
const LOG_SHOW = 40;

/** Who did what: the last actions by people, newest first. */
function WhoDidWhat({ state, rows, asked }: { state: TournamentState; rows: LoggedEvent[]; asked: boolean }) {
  if (!asked) return <p className="ad-note">Asking for the log.</p>;
  if (!rows.length) return <p className="ad-note">Nobody has done anything yet.</p>;
  return (
    <ol className="ad-log">
      {rows.map((e) => (
        <li key={e.seq}>
          <span className="ad-log-when">{clockTime(e.at)}</span>
          <span className="ad-log-what"><b>{actor(state, e.by)}:</b> {sentence(state, e)}</span>
        </li>
      ))}
    </ol>
  );
}

/** The name this phone signs its actions with, and the way to change it. */
function ThisPhone({ me }: TabProps) {
  const [changing, setChanging] = useState(false);
  // a named admin's link brings its name: it is theirs, not this phone's to change
  if (me.named) return <p className="ad-note">What you do here is logged as <b>{me.name}</b>, the name on your admin link.</p>;
  if (!me.name) return <p className="ad-note">The card at the top of the screen is asking who you are.</p>;
  if (changing) return <WhoCard was={me.name} onDone={(name) => { me.set(name); setChanging(false); }} onCancel={() => setChanging(false)} />;
  return (
    <>
      <p className="ad-note">What you do here is logged as <b>{me.name}</b>.</p>
      <button type="button" className="btn quiet ad-wide" onClick={() => setChanging(true)}>Change the name</button>
    </>
  );
}

/** The print page sits under the admin link, with the same query (so a pretend day prints a pretend day). */
function printLink(): string {
  return `${location.pathname.replace(/\/+$/, "")}/print${location.search}`;
}

/**
 * What the end of the day needs first: the three reveals in the order they are tapped, and the
 * paper fallback. Everything else is a fold.
 */
export function MoreTab(props: TabProps) {
  const { t, state, say, me } = props;
  const [word, setWord] = useState("");
  const links = t.links;
  const prizes = useMemo(() => awards(state, "admin"), [state]);
  // asked for on open and after every new event, so the closed line can say how many
  useEffect(() => {
    t.askLog(LOG_ASK);
  }, [state.seq]);
  const log = (t.log ?? []).filter((e) => /^(admin|court|remote)/.test(e.by)).slice(-LOG_SHOW).reverse();
  const linkCount = links
    ? [links.projector, links.calibration, ...links.courts, links.players, links.admin, links.watcher, links.remote].filter(Boolean).length
    : 0;

  return (
    <div className="ad-tab">
      <RevealSteps t={t} state={state} />
      {t.identity?.role === "admin" && t.identity.owner !== false && !state.revealStep ? (
        <Fold id="more.switches" title="The old reveal switches" summary="by hand">
          <RevealToggle t={t} state={state} board="awards" />
          <RevealToggle t={t} state={state} board="fun" />
          <RevealToggle t={t} state={state} board="main" />
          <p className="ad-note">The day's first way: the wall runs each one on a timer. Use the steps above instead.</p>
        </Fold>
      ) : null}

      <section className="ad-card">
        <h2>If the screens go</h2>
        <a className="btn ad-wide" href={printLink()} target="_blank" rel="noreferrer">Paper fallback</a>
        <p className="ad-note">The next rounds and the board on paper, to run the day by hand.</p>
      </section>

      <Fold id="more.who" title="Who did what" summary={t.log === null ? "asking" : `${log.length} ${log.length === 1 ? "action" : "actions"}`}>
        <WhoDidWhat state={state} rows={log} asked={t.log !== null} />
      </Fold>

      <Fold id="more.awards" title="Awards" summary={`${prizes.length} with a winner`}>
        <p className="ad-note">
          {state.reveal.awards
            ? "The wall is handing them out and their fun points count."
            : "As they stand. Their fun points count once they are handed out."}
        </p>
        {prizes.length ? (
          <ul className="ad-list ad-awards">
            {prizes.map((a) => (
              <li key={a.id} className="ad-award">
                <div className="ad-award-head">
                  <b>{a.title}</b>
                  <span className="ad-award-points">+{a.points}{a.winners.length > 1 ? " each" : ""}</span>
                </div>
                <span className="ad-award-who">{a.winners.map((id) => nameOf(state, id)).join(", ")}</span>
                <span className="ad-note">{a.detail}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ad-note">No award has a winner yet.</p>
        )}
      </Fold>

      <Fold id="more.links" title="Links" summary={links ? `${linkCount} screens` : "not here yet"}>
        {links ? (
          <ul className="ad-list ad-links">
            {links.projector && <LinkRow label="Wall" link={shortLink(links.projector, "wall", location.origin)} say={say} />}
            {links.calibration && <LinkRow label="Calibration" link={links.calibration} say={say} />}
            {links.courts.map((l, i) => <LinkRow key={l} label={`Court ${i + 1}`} link={shortLink(l, i + 1, location.origin)} say={say} />)}
            {links.players && <LinkRow label="Players" link={shortLink(links.players, "players", location.origin)} say={say} />}
            {links.admin && <LinkRow label="Owner (you)" link={links.admin} say={say} />}
            {links.watcher && <LinkRow label="Watcher" link={links.watcher} say={say} />}
            {links.remote && <LinkRow label="Watch remote" link={links.remote} say={say} />}
          </ul>
        ) : (
          <>
            <p className="ad-note">The links have not arrived yet.</p>
            <button type="button" className="btn quiet ad-wide" onClick={() => t.askLinks()}>Ask for the links</button>
          </>
        )}
      </Fold>

      <Fold id="more.admins" title="Admins" summary={links ? adminsSummary(links.admins.length, me.named) : "not here yet"}>
        <AdminsSection t={t} say={say} me={me} />
      </Fold>

      <Fold id="more.newlinks" title="New links" summary="owner and watch only">
        <p className="ad-note">For a lost phone or watch: a fresh link, and the old one stops working. The wall, court and players links stay the same all day.</p>
        <div className="ad-stack">
          {REGEN.filter((r) => r.role !== "admin" || !me.named).map((r) => (
            <ConfirmButton key={r.role} label={`New ${r.label.toLowerCase()} link`}
              question={r.question} yes="Make it"
              onConfirm={() => { t.regenerate(r.role); say(`New ${r.label.toLowerCase()} link made.`); }} />
          ))}
        </div>
      </Fold>

      <Fold id="more.rules" title="Rules" summary={`to ${state.config.target}, ${mins(state.config.gameMs / 60_000)} min games`}>
        <ConfigForm {...props} />
      </Fold>

      <Fold id="more.phone" title="This phone" summary={me.name ?? "no name yet"}>
        <ThisPhone {...props} />
      </Fold>

      <Fold id="more.reset" title="Reset the day" summary="wipes everything">
        <p className="ad-note">Wipes every round, score and change, and starts again from the player list. Type RESET to unlock it. Not while a round is being played.</p>
        <div className="ad-row">
          <input value={word} placeholder="RESET" aria-label="Type RESET" autoCapitalize="characters" autoComplete="off"
            onChange={(e) => setWord(e.target.value)} />
          <button type="button" className="btn warn" disabled={word !== "RESET"}
            onClick={() => {
              if (state.clock.phase === "playing") {
                say("A round is being played. Reset once it has finished.");
                return;
              }
              t.reset();
              setWord("");
              say("The day has been reset.");
            }}>
            Reset
          </button>
        </div>
      </Fold>
    </div>
  );
}
